import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../traderhub24-logo.svg";
import Footer from "./components/Footer";

function PrivacyPolicy({ onBack, onNavigate }: { onBack: () => void; onNavigate?: (page: "home" | "changelog" | "privacy-policy" | "terms-and-conditions" | "risk-warning" | "documentation" | "contact") => void }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-app-bg text-gray-900">
      {/* Header */}
      <header className="w-full py-3 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <img 
            src={logo} 
            alt="TraderHub24" 
            className="h-12 md:h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={onBack}
          />
          <button
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Back
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">
            Last Updated: 2025-12-17
          </p>

          <div className="prose prose-gray max-w-none space-y-6">
            <p className="text-gray-700 leading-relaxed">
              This Privacy Policy explains how we collect, use, store, and protect your personal data when you use our application and services ("TraderHub24").
            </p>
            <p className="text-gray-700 leading-relaxed">
              By accessing or using the TraderHub24, you agree to the practices described in this Privacy Policy.
            </p>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">1. Information We Collect</h2>
              <p className="text-gray-700 mb-4">
                We may collect the following categories of information:
              </p>

              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-medium mb-2 text-gray-800">a) Personal Information</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Email address</li>
                    <li>Username</li>
                    <li>Account and authentication data</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-medium mb-2 text-gray-800">b) Usage & Technical Data</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>IP address</li>
                    <li>Device type, browser type</li>
                    <li>Operating system</li>
                    <li>Log data and interaction events</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-medium mb-2 text-gray-800">c) Trading & Performance Data</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Trade entries (symbol, entry price, exit price, position size)</li>
                    <li>Risk metrics (risk/reward ratio, win rate, profit factor)</li>
                    <li>Timestamps and session-based analytics</li>
                  </ul>
                  <p className="text-gray-700 mt-2 italic">
                    This data is used strictly for analytics, reporting, and improving user experience. We do not provide financial advice.
                  </p>
                </div>
              </div>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">2. How We Use Your Information</h2>
              <p className="text-gray-700 mb-2">We use collected data to:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Provide and maintain the Service</li>
                <li>Authenticate users and manage accounts</li>
                <li>Generate performance analytics and insights</li>
                <li>Improve product functionality and usability</li>
                <li>Ensure security and prevent abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">3. Legal Basis for Processing (GDPR)</h2>
              <p className="text-gray-700 mb-2">We process personal data based on:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>User consent</li>
                <li>Performance of a contract</li>
                <li>Legitimate interests (security, analytics, service improvement)</li>
                <li>Legal obligations</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">4. Data Storage and Security</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Data is stored using secure infrastructure and encrypted connections</li>
                <li>Access is restricted to authorized systems only</li>
                <li>We apply industry-standard technical and organizational measures</li>
                <li>Despite best efforts, no system is 100% secure. Users acknowledge this inherent risk.</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">5. Data Sharing</h2>
              <p className="text-gray-700 mb-2">We do not sell or rent personal data.</p>
              <p className="text-gray-700 mb-2">Data may be shared only with:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Trusted service providers (e.g. authentication, payments, hosting)</li>
                <li>Legal authorities if required by law</li>
              </ul>
              <p className="text-gray-700 mt-2">
                All third parties are contractually bound to data protection obligations.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">6. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 mb-2">We may use cookies or similar technologies to:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Maintain sessions</li>
                <li>Analyze usage patterns</li>
                <li>Improve performance</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Users can control cookie preferences through their browser settings.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">7. User Rights (GDPR)</h2>
              <p className="text-gray-700 mb-2">You have the right to:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Request data deletion ("right to be forgotten")</li>
                <li>Restrict or object to processing</li>
                <li>Request data portability</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Requests can be submitted via the contact information below.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">8. Data Retention</h2>
              <p className="text-gray-700 mb-2">Personal data is retained only as long as necessary:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>While the account is active</li>
                <li>As required by legal or regulatory obligations</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Upon account deletion, data will be securely removed or anonymized.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">9. Third-Party Links</h2>
              <p className="text-gray-700">
                The Service may contain links to third-party websites. We are not responsible for their privacy practices or content.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">10. Children's Privacy</h2>
              <p className="text-gray-700">
                The Service is not intended for individuals under the age of 18. We do not knowingly collect data from minors.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900">11. Changes to This Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. Material changes will be communicated through the Service.
              </p>
            </section>

                      </div>
        </div>
      </main>

      {/* Footer */}
      <Footer currentPage="privacy-policy" onNavigate={onNavigate || ((page) => {
        if (page === "privacy-policy") return;
        else if (page === "terms-and-conditions") navigate("/terms");
        else if (page === "risk-warning") navigate("/risk-warning");
        else if (page === "documentation") navigate("/documentation");
        else if (page === "contact") navigate("/contact");
        else if (page === "home") onBack();
        else if (page === "changelog") navigate("/changelog");
      })} />
    </div>
  );
}

export default PrivacyPolicy;
